import { Box, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { showBelowSx } from '../collapse';
import { LEAD_WIDTH, type LedgerCollapse } from '../types';

interface LedgerRowTextProps {
  /** Generic lead slot: a status dot, a hollow dot, a deduction minus, anything. */
  lead?: ReactNode;
  title: ReactNode;
  /** Trailing slot on the title line: part tag, kind tag, percentage. */
  titleTag?: ReactNode;
  /** Single line shown only at or below the collapse point. */
  meta?: string;
  collapse: LedgerCollapse;
  /** Rendered in a tooltip anchored to the title line. */
  hoverCard?: ReactNode;
}

/**
 * Internal text block for ledger rows. Not exported from the ledger index.
 *
 * The hover card anchors to the title line rather than the row: a full-width
 * anchor pushes `right-start` off the viewport and MUI flips the card to the
 * left, away from the thing being described.
 */
export const LedgerRowText = ({
  lead,
  title,
  titleTag,
  meta,
  collapse,
  hoverCard,
}: LedgerRowTextProps) => {
  const titleLine = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          width: LEAD_WIDTH,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {lead}
      </Box>
      <Typography variant="body2" fontWeight={500} noWrap>
        {title}
      </Typography>
      {titleTag}
    </Box>
  );

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {hoverCard ? (
        <Tooltip
          title={hoverCard}
          placement="right-start"
          enterDelay={500}
          enterNextDelay={300}
          leaveDelay={100}
          slotProps={{
            tooltip: {
              sx: {
                p: 0,
                maxWidth: 320,
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 6,
              },
            },
          }}
        >
          {titleLine}
        </Tooltip>
      ) : (
        titleLine
      )}
      {meta != null && meta.length > 0 && (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ pl: `${LEAD_WIDTH + 10}px`, ...showBelowSx(collapse) }}
        >
          {meta}
        </Typography>
      )}
    </Box>
  );
};
